import { isNamedDatePeriod, type DateRangeResolver } from '@/lib/date-periods';
import {
  decodeCols,
  decodeSort,
  encodeCols,
  encodeSort,
  parseColsParam,
  type DecodedSort,
} from '@shared/report-view-state';
import {
  namedDateRange,
  parseUrlYmd,
  readDateRangeParams,
  writeDateRangeParams,
  type DateRangeParams,
} from './date-range';

/**
 * One value a page keeps in its URL, for `useUrlState` (`./use-url-state`).
 *
 * A field reads its canonical key and any old names (`keys`), falls back to its default when the
 * value is missing or invalid, and is left out of the URL when it equals the default. Writing a
 * page's URL rewrites every field, so old names and invalid values drop out on the first change.
 */
export type UrlField<T> = {
  /** Every key this field owns: the canonical one first, then old names it still reads. */
  readonly keys: readonly string[];
  /** The value when the URL has none, or an invalid one. A function, so dates follow today. */
  getDefault(): T;
  /** The value in `params`, or undefined when it's missing or invalid. */
  read(params: URLSearchParams): T | undefined;
  /** Writes a value that isn't the default. Its keys have already been deleted. */
  write(params: URLSearchParams, value: T): void;
  equals(a: T, b: T): boolean;
  /**
   * Part of the view a page remembers for a bare link back to it (decision 24 a): filters, sort,
   * period. Search, page, page size and columns are not.
   */
  readonly remember: boolean;
  /** Changing it adds a history entry: section tabs (decision 28 a). Everything else replaces. */
  readonly history: 'push' | 'replace';
  /** Changes what's shown, not which data is loaded (`cols`): left out of `urlDataKey`. */
  readonly presentation: boolean;
};

/** Options every field takes. */
export type UrlFieldOptions = {
  /** Old key names, still read so bookmarks, saved views and email links keep working. */
  aliases?: readonly string[];
  remember?: boolean;
  history?: 'push' | 'replace';
};

type ParamSpec<T> = UrlFieldOptions & {
  key: string;
  default: T | (() => T);
  /** The value for a raw URL string, or undefined when it's invalid. */
  parse(raw: string): T | undefined;
  format(value: T): string;
  equals?(a: T, b: T): boolean;
  /** Defaults to true. */
  remember?: boolean;
  presentation?: boolean;
};

/** A field kept under one key (plus its old names). The first key present wins. */
export function urlParam<T>(spec: ParamSpec<T>): UrlField<T> {
  const keys = [spec.key, ...(spec.aliases ?? [])];
  const fallback = spec.default;
  return {
    keys,
    getDefault: () =>
      typeof fallback === 'function' ? (fallback as () => T)() : fallback,
    read(params) {
      for (const key of keys) {
        const raw = params.get(key);
        if (raw !== null) return spec.parse(raw);
      }
      return undefined;
    },
    write(params, value) {
      params.set(spec.key, spec.format(value));
    },
    equals: spec.equals ?? Object.is,
    remember: spec.remember ?? true,
    history: spec.history ?? 'replace',
    presentation: spec.presentation ?? false,
  };
}

function sameList<T>(a: readonly T[], b: readonly T[]) {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * Free text, trimmed. The search box (`q`) is not remembered: it starts empty when you come back
 * (decision 24 a). Bind an input to it through `useUrlSearchDraft`, never straight.
 */
export function urlText(
  key: string,
  options: UrlFieldOptions & { default?: string } = {}
): UrlField<string> {
  return urlParam({
    key,
    aliases: options.aliases,
    remember: options.remember ?? false,
    history: options.history,
    default: options.default ?? '',
    parse: (raw) => raw.trim() || undefined,
    format: (value) => value.trim(),
  });
}

/**
 * One id or word, or null when the URL has none (the one customer a list is narrowed to, the
 * collection picked in a tree). Not remembered unless asked: it's usually a selection, not a filter.
 */
export function urlOptionalText(
  key: string,
  options: UrlFieldOptions = {}
): UrlField<string | null> {
  return urlParam<string | null>({
    ...options,
    key,
    remember: options.remember ?? false,
    default: null,
    parse: (raw) => raw.trim() || undefined,
    format: (value) => value?.trim() ?? '',
  });
}

/** One of a fixed set of values (a status tab, a mode). */
export function urlEnum<T extends string>(
  key: string,
  values: readonly T[],
  defaultValue: T,
  options: UrlFieldOptions = {}
): UrlField<T> {
  return urlParam({
    ...options,
    key,
    default: defaultValue,
    parse: (raw) => (values as readonly string[]).includes(raw) ? (raw as T) : undefined,
    format: (value) => value,
  });
}

/** One of a fixed set of values, or null for no choice (a "has / missing" filter that's off). */
export function urlOptionalEnum<T extends string>(
  key: string,
  values: readonly T[],
  options: UrlFieldOptions = {}
): UrlField<T | null> {
  return urlParam<T | null>({
    ...options,
    key,
    default: null,
    parse: (raw) => (values as readonly string[]).includes(raw) ? (raw as T) : undefined,
    format: (value) => value ?? '',
  });
}

/** A whole number from `min` to `max` (a value outside is brought inside); `default` when missing. */
export function urlInt(
  key: string,
  options: UrlFieldOptions & { min: number; max: number; default: number }
): UrlField<number> {
  return urlParam({
    ...options,
    key,
    default: options.default,
    parse(raw) {
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? Math.max(options.min, Math.min(options.max, n)) : undefined;
    },
    format: String,
  });
}

/**
 * A comma-separated list without repeats. With `values` it's a set: anything else is dropped,
 * and it's kept in the order of `values`, so picking the same statuses in another order is the
 * same URL. Without, it keeps the order chosen. An empty value (`statuses=`) is an explicit empty
 * list, for lists whose default isn't empty; a list whose only entries are invalid falls back to
 * the default.
 */
export function urlList<T extends string = string>(
  key: string,
  options: UrlFieldOptions & { values?: readonly T[]; default?: readonly T[] } = {}
): UrlField<T[]> {
  const order = options.values ? new Map<string, number>(options.values.map((v, i) => [v, i])) : null;
  const normalize = (items: readonly string[]) => {
    const unique = [...new Set(items)].filter((s) => s && (!order || order.has(s)));
    if (order) unique.sort((a, b) => order.get(a)! - order.get(b)!);
    return unique as T[];
  };
  const fallback = normalize(options.default ?? []);
  return urlParam<T[]>({
    ...options,
    key,
    default: () => [...fallback],
    parse(raw) {
      if (raw.trim() === '') return [];
      const items = normalize(raw.split(',').map((s) => s.trim()));
      return items.length ? items : undefined;
    },
    format: (value) => normalize(value).join(','),
    equals: (a, b) => sameList(normalize(a), normalize(b)),
  });
}

/** Record ids: `<entity>Ids` (resolution 7). Ids the page doesn't know are its to ignore. */
export function urlIdList(key: string, options: UrlFieldOptions = {}): UrlField<string[]> {
  return urlList(key, options);
}

/** On or off, written as `1` or `0` when it differs from the default. Reads `true`/`false` too. */
export function urlBool(
  key: string,
  options: UrlFieldOptions & { default?: boolean } = {}
): UrlField<boolean> {
  return urlParam({
    ...options,
    key,
    default: options.default ?? false,
    parse: (raw) =>
      raw === '1' || raw === 'true' ? true : raw === '0' || raw === 'false' ? false : undefined,
    format: (value) => (value ? '1' : '0'),
  });
}

/** The page number: 1 or more, left out when 1, never remembered (resolution 10). */
export function urlPage(key = 'page'): UrlField<number> {
  return urlParam({
    key,
    default: 1,
    remember: false,
    parse(raw) {
      const n = Number(raw);
      return Number.isInteger(n) && n >= 1 ? n : undefined;
    },
    format: String,
  });
}

/**
 * Page size (`limit`; reads `pageSize`). A preference: the URL value applies to this visit only,
 * and the saved size lives in storage (resolution 10), so this is never remembered here.
 */
export function urlLimit(
  options: { key?: string; aliases?: readonly string[]; values: readonly number[]; default: number }
): UrlField<number> {
  return urlParam({
    key: options.key ?? 'limit',
    aliases: options.aliases ?? ['pageSize'],
    default: options.default,
    remember: false,
    parse(raw) {
      const n = Number(raw);
      return options.values.includes(n) ? n : undefined;
    },
    format: String,
  });
}

/** A day, YYYY-MM-DD; '' when unset. A from/to pair is `urlDateRange` instead. */
export function urlYmd(key: string, options: UrlFieldOptions = {}): UrlField<string> {
  return urlParam({
    ...options,
    key,
    default: '',
    parse: (raw) => (parseUrlYmd(raw) ? raw.trim() : undefined),
    format: (value) => value,
  });
}

/**
 * `sort=<columnId>.<asc|desc>` (resolution 1). `legacy` reads the two keys older list pages used
 * (`sortField` and `sortDirection` with `ascending`/`descending`), so their links keep working.
 */
export function urlSort(options: {
  key?: string;
  columns: readonly string[];
  default: DecodedSort;
  legacy?: { field: string; direction: string };
  remember?: boolean;
}): UrlField<DecodedSort> {
  const key = options.key ?? 'sort';
  const legacy = options.legacy;
  const field = urlParam<DecodedSort>({
    key,
    remember: options.remember,
    default: () => ({ ...options.default }),
    parse(raw) {
      // decodeSort gives the default for anything invalid; tell the two apart.
      const sentinel = { id: '', dir: 'asc' } as const;
      const sort = decodeSort(raw, options.columns, sentinel);
      return sort === sentinel ? undefined : sort;
    },
    format: (value) => encodeSort(value.id, value.dir),
    equals: (a, b) => a.id === b.id && a.dir === b.dir,
  });
  if (!legacy) return field;
  return {
    ...field,
    keys: [key, legacy.field, legacy.direction],
    read(params) {
      if (params.has(key)) return field.read(params);
      const id = params.get(legacy.field);
      if (id === null) return undefined;
      const rawDir = params.get(legacy.direction) ?? 'asc';
      const dir =
        rawDir === 'asc' || rawDir === 'ascending'
          ? 'asc'
          : rawDir === 'desc' || rawDir === 'descending'
            ? 'desc'
            : null;
      return dir && options.columns.includes(id) ? { id, dir } : undefined;
    },
  };
}

/**
 * Visible columns, in the bitmask form of `shared/report-view-state.ts`. Only a saved view writes
 * it; ordinary column choices are a preference in storage. Marked presentation, so a data cache
 * keyed with `urlDataKey` doesn't change when columns do.
 */
export function urlCols(allIds: readonly string[], key = 'cols'): UrlField<string[]> {
  return urlParam<string[]>({
    key,
    default: () => [...allIds],
    remember: false,
    presentation: true,
    parse: (raw) => (parseColsParam(raw) ? decodeCols(allIds, raw) : undefined),
    format: (value) => encodeCols(allIds, value) ?? '',
    equals: sameList,
  });
}

/**
 * Columns a link shows for that visit (a saved view, an email link), as its bitmask: null when the
 * URL has none, so the person's saved choice shows. Kept raw and decoded where it's used
 * (`decodeCols(ids, mask)`), for pages whose columns depend on another field (Products by variant
 * or by product). Ordinary column choices go to storage, not here (report-column-prefs.mdc).
 */
export function urlColsMask(key = 'cols'): UrlField<string | null> {
  return urlParam<string | null>({
    key,
    default: null,
    remember: false,
    presentation: true,
    parse: (raw) => (parseColsParam(raw) ? raw.trim() : undefined),
    format: (value) => value ?? '',
  });
}

/** The mask for `visible` of `allIds`, for `urlColsMask`: every column is the full mask, not ''. */
export function colsMaskFor(allIds: readonly string[], visible: readonly string[]): string {
  return (
    encodeCols(allIds, [...visible]) ??
    ((1n << BigInt(allIds.length)) - 1n).toString(16)
  );
}

/**
 * The query a saved view stores: the URL, plus the columns on screen when the URL has no `cols`
 * (ordinary browsing keeps columns in storage, not the URL), so applying the view brings them back.
 */
export function savedViewQuery(
  searchParams: URLSearchParams,
  allIds: readonly string[],
  visible: readonly string[]
): string {
  if (searchParams.has('cols')) return searchParams.toString();
  const next = new URLSearchParams(searchParams);
  next.set('cols', colsMaskFor(allIds, visible));
  return next.toString();
}

/**
 * A date range on `period`, `from` and `to` (`./date-range`): a named period is stored by name
 * and recomputed from today; only Custom keeps its dates (decision 25 a).
 */
export function urlDateRange(options: {
  defaultPeriod: string;
  resolve?: DateRangeResolver;
  remember?: boolean;
}): UrlField<DateRangeParams> {
  const resolveOptions = { resolve: options.resolve };
  return {
    keys: ['period', 'from', 'to'],
    getDefault: () => namedDateRange(options.defaultPeriod, resolveOptions),
    read(params) {
      if (!params.has('period') && !params.has('from') && !params.has('to')) return undefined;
      return readDateRangeParams(params, options.defaultPeriod, resolveOptions);
    },
    write: writeDateRangeParams,
    equals: (a, b) =>
      a.period === b.period && (isNamedDatePeriod(a.period) || (a.from === b.from && a.to === b.to)),
    remember: options.remember ?? true,
    history: 'replace',
    presentation: false,
  };
}
